import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Loader2, Eye, Plus, ArrowUpDown, ArrowUp, ArrowDown,
  Database, Calendar, Layers, ArrowLeft, ChevronDown, ChevronRight,
  BookOpen, ShieldCheck, FolderKanban, Table2, Save,
} from 'lucide-react';
import StepIndicator from '../onboarding/StepIndicator';
import { profilingApi } from '../../services/api';
import { formatDate, formatDateTime } from '../../utils/format';
import PaginationBar from '../../components/PaginationBar';
import type { ProfilingSessionHistory, Pagination } from '../../types';

type SortDir = 'asc' | 'desc';

interface ProfilingLandingProps {
  onNewSession: () => void;
}

export default function ProfilingLanding({ onNewSession }: ProfilingLandingProps) {
  const [items, setItems] = useState<ProfilingSessionHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({ count: 0, page: 1, per_page: 10, pages: 1 });
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [viewId, setViewId] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await profilingApi.history({ page: pagination.page, items_per_page: pagination.per_page, search, sort_col: sortCol, sort_dir: sortDir });
      const data = resp.data.payload;
      setItems(data.items);
      setPagination(data.pagination);
    } catch (err) { console.error('Failed to load profiling history', err); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.per_page, search, sortCol, sortDir]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPagination(p => ({ ...p, page: 1 }));
  };

  if (viewId) return <ProfilingFlowView sessionId={viewId} onBack={() => setViewId(null)} onNewSession={onNewSession} />;

  const totalSessions = pagination.count;
  const totalTables = items.reduce((sum, s) => sum + (s.table_count || 0), 0);
  const lastSession = items.length > 0 ? items[0]?.timestamp : null;

  function SortIcon({ col }: { col: string }) {
    if (sortCol !== col) return <ArrowUpDown className="w-3.5 h-3.5 text-cargo-muted/40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-cargo-accent" /> : <ArrowDown className="w-3.5 h-3.5 text-cargo-accent" />;
  }

  return (
    <div className="space-y-6">
      <button onClick={onNewSession}
        className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-cargo-surface border border-cargo-border hover:border-cargo-accent/50 hover:bg-cargo-accent/5 transition-all group">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cargo-accent/15 flex items-center justify-center">
            <Plus className="w-5 h-5 text-cargo-accent" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-cargo-text">New Profiling Assessment</p>
            <p className="text-xs text-cargo-muted">Connect, profile, generate dictionary, and auto-generate rules</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-cargo-muted group-hover:text-cargo-accent transition-colors" />
      </button>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard icon={<Layers className="w-5 h-5 text-cargo-accent" />} label="Total Sessions" value={String(totalSessions)} />
        <SummaryCard icon={<Database className="w-5 h-5 text-blue-400" />} label="Total Tables Profiled" value={String(totalTables)} />
        <SummaryCard icon={<Calendar className="w-5 h-5 text-emerald-400" />} label="Last Session" value={lastSession ? formatDate(lastSession) : '—'} />
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
          <input type="text" value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            placeholder="Search projects, groups..."
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-cargo-surface border border-cargo-border text-cargo-text text-sm placeholder:text-cargo-muted outline-none focus:border-cargo-accent" />
        </div>
      </div>

      <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-cargo-border">
              {[{ col: 'project_name', label: 'Project' }, { col: 'group_name', label: 'Group' },
                { col: 'table_count', label: 'Tables' }, { col: 'column_count', label: 'Columns' },
                { col: 'rule_count', label: 'Rules Generated' }, { col: 'connection_type', label: 'Connection' },
                { col: 'timestamp', label: 'Date' }].map(({ col, label }) => (
                <th key={col} className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider cursor-pointer hover:text-cargo-text"
                  onClick={() => handleSort(col)}>
                  <span className="flex items-center gap-1">{label} <SortIcon col={col} /></span>
                </th>
              ))}
              <th className="px-4 py-3 text-xs font-medium text-cargo-muted uppercase tracking-wider">Actions</th>
            </tr></thead>
            <tbody className="divide-y divide-cargo-border">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-cargo-accent mx-auto" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-cargo-muted">No profiling assessments found.</td></tr>
              ) : items.map((s) => (
                <tr key={s._id} className="hover:bg-cargo-hover/30 transition-colors">
                  <td className="px-4 py-3 text-cargo-text font-medium">{s.project_name}</td>
                  <td className="px-4 py-3 text-cargo-muted">{s.group_name}</td>
                  <td className="px-4 py-3 text-cargo-text font-medium">{s.table_count}</td>
                  <td className="px-4 py-3 text-cargo-muted">{s.column_count}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border bg-blue-500/20 text-blue-400 border-blue-500/30">{s.rule_count}</span></td>
                  <td className="px-4 py-3"><span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded bg-cargo-panel text-cargo-muted border border-cargo-border uppercase">{s.connection_type || '—'}</span></td>
                  <td className="px-4 py-3 text-cargo-muted">{formatDate(s.timestamp)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setViewId(s._id)} className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-accent hover:bg-cargo-accent/10 transition-colors" title="View full flow">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && items.length > 0 && (
          <PaginationBar page={pagination.page} totalPages={pagination.pages} perPage={pagination.per_page}
            onPageChange={(p) => setPagination(prev => ({ ...prev, page: p }))}
            onPerPageChange={(n) => setPagination(prev => ({ ...prev, per_page: n, page: 1 }))} />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   Profiling Full Flow Read-Only View
   ═══════════════════════════════════════ */

const FLOW_STEPS = ['Connect', 'Tables', 'Profile', 'Dictionary', 'Rules', 'Save'];

function ProfilingFlowView({ sessionId, onBack, onNewSession }: { sessionId: string; onBack: () => void; onNewSession: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  useEffect(() => {
    setLoading(true);
    profilingApi.historyDetail(sessionId)
      .then((resp) => setData(resp.data.payload))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-cargo-accent" /></div>;
  if (!data) return <div className="text-center py-24 text-cargo-muted">Failed to load session data.</div>;

  const flow = data.flow || {};
  const profile = data.profile_result;
  const dict = data.dictionary_result;
  const rules = data.suggested_rules || [];

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="mb-6 flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-cargo-text">Profiling Assessment — {data.project_name}</h1>
          <p className="text-cargo-muted text-sm">{formatDateTime(data.timestamp)}</p>
        </div>
      </div>

      <StepIndicator steps={FLOW_STEPS} current={step} />

      <div className="mt-8">
        {/* Step 0: Connect */}
        {step === 0 && (
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6 space-y-4">
            <h2 className="text-lg font-semibold text-cargo-text">Connection</h2>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-xl bg-cargo-accent/10 flex items-center justify-center">
                <Database className="w-8 h-8 text-cargo-accent" />
              </div>
              <div>
                <p className="text-lg font-bold text-cargo-text uppercase">{flow.connector_type || data.connection_type || '—'}</p>
                <p className="text-sm text-cargo-muted">Database connector</p>
              </div>
            </div>
            {flow.connection && (
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(flow.connection).filter(([k]) => k !== 'password' && k !== 'connector_type').map(([key, val]) => (
                  <div key={key} className="bg-cargo-panel rounded-lg p-3">
                    <p className="text-xs text-cargo-muted capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-sm font-medium text-cargo-text mt-0.5">{String(val || '—')}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 1: Tables */}
        {step === 1 && (
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6">
            <h2 className="text-lg font-semibold text-cargo-text mb-4">Selected Tables</h2>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <InfoCard icon={<ShieldCheck className="w-5 h-5 text-purple-400" />} label="Schema" value={data.schema || flow.schema || '—'} />
              <InfoCard icon={<Table2 className="w-5 h-5 text-cargo-success" />} label="Tables" value={`${data.table_count || 0} selected`} />
            </div>
            <div className="flex flex-wrap gap-2">
              {(data.tables || []).map((t: string) => (
                <span key={t} className="px-3 py-1.5 bg-cargo-panel rounded-full text-sm text-cargo-text border border-cargo-border">{t}</span>
              ))}
              {(data.selected_tables || []).filter((t: any) => !(data.tables || []).includes(t.table_name)).map((t: any) => (
                <span key={t.table_name} className="px-3 py-1.5 bg-cargo-panel rounded-full text-sm text-cargo-text border border-cargo-border">
                  {t.schema_name}.{t.table_name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Profile */}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-cargo-text">Profile Results</h2>
            {!profile?.tables?.length ? (
              <p className="text-cargo-muted bg-cargo-surface rounded-xl border border-cargo-border p-12 text-center">No profile data saved for this session.</p>
            ) : profile.tables.map((table: any) => (
              <ExpandableTable key={table.table_name} title={table.table_name} subtitle={`${table.row_count?.toLocaleString()} rows • ${table.columns?.length} columns`}>
                <table className="w-full text-xs">
                  <thead><tr className="bg-cargo-panel border-t border-cargo-border">
                    <th className="px-3 py-2 text-left">Column</th><th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-center">Null %</th><th className="px-3 py-2 text-center">Unique %</th>
                    <th className="px-3 py-2 text-center">Min</th><th className="px-3 py-2 text-center">Max</th><th className="px-3 py-2 text-center">Mean</th>
                  </tr></thead>
                  <tbody className="divide-y divide-cargo-border">
                    {table.columns.map((col: any) => {
                      const p = col.profiling || {};
                      return (
                        <tr key={col.column_name} className="hover:bg-cargo-hover/20">
                          <td className="px-3 py-2 text-cargo-text font-medium">{col.column_name}</td>
                          <td className="px-3 py-2 text-cargo-muted">{col.data_type}</td>
                          <td className="px-3 py-2 text-center"><NullBadge pct={p.null_percentage} /></td>
                          <td className="px-3 py-2 text-center text-cargo-muted">{p.unique_percentage != null ? `${p.unique_percentage.toFixed(1)}%` : '—'}</td>
                          <td className="px-3 py-2 text-center text-cargo-muted">{p.min ?? '—'}</td>
                          <td className="px-3 py-2 text-center text-cargo-muted">{p.max ?? '—'}</td>
                          <td className="px-3 py-2 text-center text-cargo-muted">{p.mean != null ? (typeof p.mean === 'number' ? p.mean.toFixed(2) : p.mean) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ExpandableTable>
            ))}
          </div>
        )}

        {/* Step 3: Dictionary */}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-cargo-text">Data Dictionary</h2>
            {!dict?.tables?.length ? (
              <p className="text-cargo-muted bg-cargo-surface rounded-xl border border-cargo-border p-12 text-center">No dictionary data saved for this session.</p>
            ) : dict.tables.map((table: any) => (
              <ExpandableTable key={table.table_name} title={table.table_name} subtitle={`${table.columns?.length} columns`}>
                <table className="w-full text-xs">
                  <thead><tr className="bg-cargo-panel border-t border-cargo-border">
                    <th className="px-3 py-2 text-left">Column</th><th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-center">PK</th><th className="px-3 py-2 text-left">Description</th>
                  </tr></thead>
                  <tbody className="divide-y divide-cargo-border">
                    {table.columns.map((col: any) => (
                      <tr key={col.name} className="hover:bg-cargo-hover/20">
                        <td className="px-3 py-2 text-cargo-text font-medium">{col.name}</td>
                        <td className="px-3 py-2 text-cargo-muted">{col.data_type}</td>
                        <td className="px-3 py-2 text-center">{col.primary_key ? <span className="text-emerald-400">&#10003;</span> : ''}</td>
                        <td className="px-3 py-2 text-cargo-muted">{col.description || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ExpandableTable>
            ))}
          </div>
        )}

        {/* Step 4: Rules */}
        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-cargo-text">Generated Rules ({rules.length})</h2>
            {rules.length === 0 ? (
              <p className="text-cargo-muted bg-cargo-surface rounded-xl border border-cargo-border p-12 text-center">No rules data saved for this session.</p>
            ) : (
              <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-cargo-panel border-b border-cargo-border">
                      <th className="px-3 py-2.5 text-left">Rule</th><th className="px-3 py-2.5 text-left">Column</th>
                      <th className="px-3 py-2.5 text-left">Table</th><th className="px-3 py-2.5 text-center">Category</th>
                      <th className="px-3 py-2.5 text-center">Confidence</th>
                    </tr></thead>
                    <tbody className="divide-y divide-cargo-border">
                      {rules.map((rule: any, idx: number) => (
                        <tr key={idx} className="hover:bg-cargo-hover/20">
                          <td className="px-3 py-2 text-cargo-text font-medium">{rule.display_name || rule.rule_name}</td>
                          <td className="px-3 py-2 text-cargo-muted">{rule.column_name || '—'}</td>
                          <td className="px-3 py-2 text-cargo-muted">{rule.table_name || '—'}</td>
                          <td className="px-3 py-2 text-center"><span className="px-2 py-0.5 rounded text-[10px] font-medium bg-cargo-panel text-cargo-muted border border-cargo-border">{rule.category || '—'}</span></td>
                          <td className="px-3 py-2 text-center"><span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                            rule.confidence === 'high' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' :
                            rule.confidence === 'medium' ? 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30' :
                            'bg-gray-500/15 text-gray-400 border-gray-500/30'
                          }`}>{rule.confidence || '—'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Save */}
        {step === 5 && (
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6 space-y-4">
            <h2 className="text-lg font-semibold text-cargo-text">Saved Project</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <InfoCard icon={<FolderKanban className="w-5 h-5 text-cargo-accent" />} label="Project" value={data.project_name} />
              <InfoCard icon={<Layers className="w-5 h-5 text-cargo-warning" />} label="Group" value={data.group_name} />
              <InfoCard icon={<BookOpen className="w-5 h-5 text-cargo-success" />} label="Rules" value={`${data.rule_count || rules.length} saved`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-cargo-panel rounded-lg p-3">
                <p className="text-xs text-cargo-muted">Connection</p>
                <p className="text-sm font-medium text-cargo-text mt-0.5 uppercase">{data.connection_type || '—'}</p>
              </div>
              <div className="bg-cargo-panel rounded-lg p-3">
                <p className="text-xs text-cargo-muted">Date</p>
                <p className="text-sm font-medium text-cargo-text mt-0.5">{formatDateTime(data.timestamp)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button onClick={() => step > 0 ? setStep(step - 1) : onBack()}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-cargo-border text-cargo-muted hover:text-cargo-text transition-colors">
          <ArrowLeft className="w-4 h-4" /> {step === 0 ? 'Back to History' : 'Back'}
        </button>
        {step < 5 && (
          <button onClick={() => setStep(step + 1)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cargo-accent hover:bg-cargo-accent-hover text-white font-medium transition-colors">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <button onClick={onNewSession}
        className="w-full flex items-center justify-between mt-6 px-5 py-4 rounded-xl bg-cargo-surface border border-cargo-border hover:border-cargo-accent/50 hover:bg-cargo-accent/5 transition-all group">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cargo-accent/15 flex items-center justify-center">
            <Plus className="w-5 h-5 text-cargo-accent" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-cargo-text">New Profiling Assessment</p>
            <p className="text-xs text-cargo-muted">Start a new source profiling assessment</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-cargo-muted group-hover:text-cargo-accent transition-colors" />
      </button>
    </div>
  );
}

/* ── Shared Components ── */

function ExpandableTable({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-cargo-border rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 bg-cargo-surface hover:bg-cargo-hover/30 transition-colors">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-cargo-muted" /> : <ChevronRight className="w-4 h-4 text-cargo-muted" />}
          <span className="text-sm font-medium text-cargo-text">{title}</span>
        </div>
        <span className="text-xs text-cargo-muted">{subtitle}</span>
      </button>
      {open && <div className="overflow-x-auto">{children}</div>}
    </div>
  );
}

function NullBadge({ pct }: { pct?: number }) {
  if (pct == null) return <span className="text-cargo-muted">—</span>;
  const color = pct === 0 ? 'text-emerald-400' : pct < 5 ? 'text-blue-400' : pct < 20 ? 'text-yellow-400' : 'text-red-400';
  return <span className={`font-medium ${color}`}>{pct.toFixed(1)}%</span>;
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 bg-cargo-panel rounded-lg p-3">
      {icon}
      <div>
        <p className="text-xs text-cargo-muted">{label}</p>
        <p className="text-sm font-medium text-cargo-text truncate">{value}</p>
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5 flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-cargo-panel flex items-center justify-center">{icon}</div>
      <div>
        <p className="text-xs text-cargo-muted">{label}</p>
        <p className="text-lg font-bold text-cargo-text">{value}</p>
      </div>
    </div>
  );
}
